import { Select } from 'antd';
import { orderBy } from 'lodash';
import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllDamageSchemasQuery } from 'types/atlas-graphql';
import { storage } from 'utils/storage';

type SchemaSelectProps = {
  selected?: string;
  setSelected: (s: string) => void;
  style?: object;
  alwaysVisible?: boolean;
  storageKey?: string; // Providing storageKey will result in persistence of the selection on an instance of this component
  setSchemaVisible?: Dispatch<SetStateAction<boolean>>; // Used for rendering on parent component
};

export const SchemaSelect: React.FunctionComponent<SchemaSelectProps> = ({
  selected,
  setSelected,
  style,
  alwaysVisible,
  storageKey: _storageKey,
  setSchemaVisible,
}) => {
  const { t } = useTranslation();
  const { data, loading } = useAllDamageSchemasQuery();

  const storageKey = _storageKey && `${_storageKey}::${storage.getItem('customerId')}`;

  const damageSchemas = useMemo(() => {
    return data?.damageSchemas || [];
  }, [data]);

  const schemaOptions = useMemo(() => {
    return orderBy(damageSchemas, [(schema) => schema?.name?.toLowerCase()], ['asc']).map(
      ({ id, name, isDefault }) => ({
        value: id,
        name,
        label: `${name && name.length ? name : t('settings.damageSchema', 'Damage Schema')}${
          isDefault ? ` (${t('table.default', 'Default')})` : ''
        }`,
      })
    );
  }, [damageSchemas, t]);

  // get the persisted schema value
  const defaultValue = useMemo(() => {
    if (storageKey) {
      const storedValue = storage.getItem(storageKey);
      if (storedValue) {
        return storedValue;
      }
    }
    return damageSchemas.find(({ isDefault }: any) => {
      return isDefault;
    })?.id;
  }, [storageKey, damageSchemas]);

  // sets the default value into state (only if it is currently null)
  useEffect(() => {
    if (!selected) {
      setSelected(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, defaultValue]);

  // when schema is updated, save it to storage
  useEffect(() => {
    if (storageKey && selected) {
      storage.setItem(storageKey, selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const hasSchemas = !loading && Boolean(damageSchemas.length);

  useEffect(() => {
    if (setSchemaVisible && (alwaysVisible || (hasSchemas && damageSchemas.length > 1))) {
      setSchemaVisible(true);
    }
  }, [setSchemaVisible, alwaysVisible, hasSchemas, damageSchemas]);

  return (
    <>
      {(alwaysVisible || (hasSchemas && damageSchemas.length > 1)) && (
        <>
          <Select
            showSearch
            filterOption={(value, option) =>
              value.length > 2 ? !!option?.name?.toLowerCase().includes(value?.toLowerCase()) : true
            }
            disabled={loading || !hasSchemas}
            style={style || { width: '25em', marginRight: '1em' }}
            popupMatchSelectWidth={false}
            defaultValue={defaultValue}
            onSelect={setSelected}
            loading={loading}
            placeholder={t('damage.selectDamageSchema', 'Select a damage schema')}
            value={selected}
            options={schemaOptions}
          />
        </>
      )}
    </>
  );
};
